import useCustomCursor from "@/components/custom-cursor/useCustomCursor";
import CustomFooter from "@/components/custom-footer/customFooter";
import CustomOffcanvas from "@/components/custom-offcanvas/customOffcanvas";
import ThemeToggle from "@/components/theme-toggle/themeToggle";
import { useAppDispatch, useAppSelector } from "@/hooks/useReduxHooks";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router";
import {
  isDarkMode as themeIsDarkMode,
  toggleTheme as themeToggle,
  onThemeChange as onThemeChange,
} from "@/utils/theme";
import type { RootState } from "@/core/redux/store";
import {
  resetMobileSidebar,
  setMobileSidebar,
  type SidebarState,
} from "@/core/redux/sidebarSlice";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useBootstrapTooltips } from "@/hooks/useBootstrapTooltips";
import { useMobileSidebarOverlay } from "@/hooks/useMobileSidebarOverlay";
import { all_routes } from "@/routes/all_routes";
import CommonHeader from "@/components/common-header/commonHeader";

const CommonLayout = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isDarkMode, setIsDarkMode] = useState(() => themeIsDarkMode());
  useCustomCursor();

  // Toggle theme function (delegates to shared util)
  const toggleTheme = () => {
    themeToggle();
  };

  // Keep local state in sync with the shared theme util
  useEffect(() => {
    const unsub = onThemeChange((isDark) => setIsDarkMode(isDark));
    return unsub;
  }, []);

  const { mobileSidebar } = useAppSelector(
    (state: RootState) => state.sidebar as SidebarState
  );

  // Ref for the main wrapper to detect clicks outside sidebar
  const mainWrapperRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const isMobile = useIsMobile();

  // Check if current page is layout-fullwidth
  const isLayoutFullwidth = location.pathname === "/layout-fullwidth";

  // Centralized tooltip initialization/cleanup
  useBootstrapTooltips([location.pathname]);

  // Centralized mobile sidebar overlay and outside click/touch handling
  useMobileSidebarOverlay({
    mobileSidebar,
    isLayoutFullwidth,
    isMobile,
    containerRef: mainWrapperRef,
    onClose: () => dispatch(setMobileSidebar(false)),
  });

  useEffect(() => {
    dispatch(resetMobileSidebar());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const body = document.body;

    // Remove any existing theme-* classes to avoid stale theme remaining
    const existingThemeClasses = Array.from(body.classList).filter((c) =>
      /^theme-\d+$/.test(c)
    );
    existingThemeClasses.forEach((c) => body.classList.remove(c));

    // Determine theme for current route
    let themeToAdd = "";
    switch (location.pathname) {
      case all_routes.doctorChat:
        themeToAdd = "main-chat-blk";
        break;
      case all_routes.chat:
        themeToAdd = "main-chat-blk";
        break;

      default:
        themeToAdd = "";
        break;
    }

    if (themeToAdd) body.classList.add(themeToAdd);
  }, [location.pathname]);
  return (
    <div className="main-wrapper">
      <CommonHeader />
      <Outlet />
      <CustomFooter />
      <CustomOffcanvas />
      <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleTheme} />

      {/* Cursor */}
      <div className="mouse-cursor cursor-outer" />
      <div className="mouse-cursor cursor-inner" />
      {/* /Cursor */}
    </div>
  );
};

export default CommonLayout;
